import React, { useState, useEffect, useMemo, useRef } from 'react';
import AdminNav from '../components/AdminNav';
import { getVolunteers, getAssignments } from '../services/firebase';
import './Admin.css';

// ─── Language detection (same logic as AvailabilityRequests) ──────────────────
function isSpanish(congregation) {
  return /spanish/i.test(congregation || '');
}

// ─── Markdown → HTML converter (bold, italic, ul, ol, paragraphs) ─────────────
function markdownToHtml(text) {
  if (!text) return '';

  // Process line by line, grouping into blocks
  const lines = text.split('\n');
  const out = [];
  let ulOpen = false;
  let olOpen = false;

  const closeUl = () => { if (ulOpen) { out.push('</ul>'); ulOpen = false; } };
  const closeOl = () => { if (olOpen) { out.push('</ol>'); olOpen = false; } };

  const inlineFormat = (line) =>
    line
      // Bold: **text** or __text__
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      // Italic: *text* or _text_ (not inside **)
      .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
      .replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, '<em>$1</em>');

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const trimmed = raw.trim();

    // Bullet list item
    if (/^[-*•]\s+/.test(trimmed)) {
      closeOl();
      if (!ulOpen) { out.push('<ul style="margin:8px 0 8px 24px;padding:0;">'); ulOpen = true; }
      out.push(`<li style="margin-bottom:4px;">${inlineFormat(trimmed.replace(/^[-*•]\s+/, ''))}</li>`);
      continue;
    }

    // Numbered list item
    if (/^\d+\.\s+/.test(trimmed)) {
      closeUl();
      if (!olOpen) { out.push('<ol style="margin:8px 0 8px 24px;padding:0;">'); olOpen = true; }
      out.push(`<li style="margin-bottom:4px;">${inlineFormat(trimmed.replace(/^\d+\.\s+/, ''))}</li>`);
      continue;
    }

    // Empty line = paragraph break
    if (trimmed === '') {
      closeUl();
      closeOl();
      out.push('<br>');
      continue;
    }

    // Regular line
    closeUl();
    closeOl();
    out.push(`<p style="margin:0 0 10px;line-height:1.65;">${inlineFormat(trimmed)}</p>`);
  }

  closeUl();
  closeOl();

  return out.join('\n');
}

// ─── Default message content ──────────────────────────────────────────────────
const DEFAULT = {
  en: {
    subject: 'SMPW Masters Event 2026 — Update From Our Scheduling Team',
    body: `Dear [Volunteer Name],

Thank you so much for your interest and willingness to serve at this year's SMPW Masters Event in Augusta! We truly appreciate everyone who took the time to respond.

We want to provide a brief update on where things stand. At this time, all shift assignments have been made. If you were assigned to a shift, you should have received a separate email from us asking you to confirm your assignment. If you haven't done so yet, please check your inbox (and your spam/junk folder) and confirm at your earliest convenience — approximately 20 volunteers have yet to confirm.

If those who have not confirmed are unable to do so, we may reach out to additional volunteers to fill any remaining openings. In addition, the following shift slots still have openings available:

- Thursday, April 9 — 10 AM–1 PM (1 opening)
- Thursday, April 9 — 1–3 PM (2 openings)
- Thursday, April 9 — 3–5 PM (1 opening)
- Friday, April 10 — 10 AM–1 PM (1 opening)
- Friday, April 10 — 1–3 PM (2 openings)
- Sunday, April 12 — 5–8 PM (1 opening)

Thank you again for your desire to serve at this event. We look forward to the continued growth of this work and hope to have even more opportunities for brothers and sisters to participate in future years!

With warm Christian love,
SMPW Scheduling Team – Augusta Masters`,
  },
  es: {
    subject: 'Evento SMPW Masters 2026 — Actualización de Nuestro Equipo de Programación',
    body: `Estimado/a [Volunteer Name],

¡Muchas gracias por su interés y disposición para servir en el Evento SMPW Masters de este año en Augusta! Apreciamos sinceramente a todos los que tomaron el tiempo de responder.

Queremos proporcionar una breve actualización sobre cómo van las cosas. En este momento, todas las asignaciones de turnos han sido realizadas. Si fue asignado/a a un turno, debería haber recibido un correo electrónico separado de nuestra parte solicitándole confirmar su asignación. Si aún no lo ha hecho, por favor revise su bandeja de entrada (y su carpeta de spam/correo no deseado) y confirme a la brevedad posible — aproximadamente 20 voluntarios aún no han confirmado.

Si quienes no han confirmado no pueden hacerlo, es posible que nos comuniquemos con voluntarios adicionales para cubrir los puestos restantes. Además, los siguientes turnos aún tienen disponibilidad:

- Jueves, 9 de abril — 10 AM–1 PM (1 disponible)
- Jueves, 9 de abril — 1–3 PM (2 disponibles)
- Jueves, 9 de abril — 3–5 PM (1 disponible)
- Viernes, 10 de abril — 10 AM–1 PM (1 disponible)
- Viernes, 10 de abril — 1–3 PM (2 disponibles)
- Domingo, 12 de abril — 5–8 PM (1 disponible)

¡Gracias nuevamente por su deseo de servir en este evento! Esperamos el continuo crecimiento de esta obra y esperamos tener aún más oportunidades para que los hermanos y hermanas participen en años futuros.

Con cariño cristiano,
Equipo de Programación SMPW – Augusta Masters`,
  },
};

// ─── Volunteer status helpers ─────────────────────────────────────────────────
function getVolStatus(vol, shiftKeys) {
  if (!shiftKeys || shiftKeys.length === 0) return 'unassigned';
  const statuses = shiftKeys.map(key => {
    const shiftId = key.replace(/\s+/g, '_');
    return vol[`confirmation_${shiftId}`] || 'pending';
  });
  if (statuses.every(s => s === 'confirmed')) return 'confirmed';
  if (statuses.some(s => s === 'cannot-make')) return 'cannot-make';
  return 'pending';
}

const STATUS_META = {
  unassigned:    { label: 'Not Assigned',        color: '#64748b', bg: '#f1f5f9' },
  confirmed:     { label: 'Confirmed',            color: '#15803d', bg: '#f0fdf4' },
  pending:       { label: 'Pending Confirmation', color: '#92400e', bg: '#fef3c7' },
  'cannot-make': { label: 'Cannot Make',          color: '#b91c1c', bg: '#fef2f2' },
};

const ALL_FILTERS = [
  { key: 'unassigned',   label: 'Not Assigned' },
  { key: 'pending',      label: 'Pending Confirmation' },
  { key: 'cannot-make',  label: 'Cannot Make' },
  { key: 'confirmed',    label: 'Confirmed' },
];

// ─── Toolbar helper: wrap/unwrap selection in a textarea ──────────────────────
function applyFormatting(textarea, type) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const value = textarea.value;
  const selected = value.slice(start, end);

  let newValue, newStart, newEnd;

  if (type === 'bold') {
    const wrapped = `**${selected}**`;
    newValue = value.slice(0, start) + wrapped + value.slice(end);
    newStart = selected ? start : start + 2;
    newEnd = selected ? start + wrapped.length : newStart;
  } else if (type === 'italic') {
    const wrapped = `*${selected}*`;
    newValue = value.slice(0, start) + wrapped + value.slice(end);
    newStart = selected ? start : start + 1;
    newEnd = selected ? start + wrapped.length : newStart;
  } else if (type === 'ul') {
    // Prefix each selected line with "- "
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf('\n') + 1;
    const fullSelection = value.slice(lineStart, end);
    const transformed = fullSelection.split('\n').map(l =>
      l.startsWith('- ') ? l : `- ${l}`
    ).join('\n');
    newValue = value.slice(0, lineStart) + transformed + value.slice(end);
    newStart = lineStart;
    newEnd = lineStart + transformed.length;
  } else if (type === 'ol') {
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf('\n') + 1;
    const fullSelection = value.slice(lineStart, end);
    let idx = 0;
    const transformed = fullSelection.split('\n').map(l => {
      if (/^\d+\.\s/.test(l)) return l;
      idx++;
      return `${idx}. ${l}`;
    }).join('\n');
    newValue = value.slice(0, lineStart) + transformed + value.slice(end);
    newStart = lineStart;
    newEnd = lineStart + transformed.length;
  }

  return { newValue, newStart, newEnd };
}

// ─── Component ────────────────────────────────────────────────────────────────
function BroadcastEmail({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [volShifts, setVolShifts] = useState({});
  const [loading, setLoading] = useState(true);

  // Multi-group selection (Set of status keys, empty = all)
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set()); // volunteer IDs

  // Compose — separate state per language tab
  const [lang, setLang] = useState('en');
  const [subjects, setSubjects] = useState({ en: DEFAULT.en.subject, es: DEFAULT.es.subject });
  const [bodies, setBodies] = useState({ en: DEFAULT.en.body, es: DEFAULT.es.body });
  const [preview, setPreview] = useState(false);

  // Send state
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const bodyRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [volData, assignData] = await Promise.all([
      getVolunteers(),
      getAssignments(),
    ]);
    const shifts = {};
    assignData.forEach(a => {
      const key = `${a.day}_${a.location}_${a.shiftTime}`;
      (a.volunteers || []).filter(Boolean).forEach(id => {
        if (!shifts[id]) shifts[id] = [];
        shifts[id].push(key);
      });
    });
    setVolunteers(volData);
    setVolShifts(shifts);
    setLoading(false);
  };

  // Enriched list with computed status + language
  const enriched = useMemo(() => {
    return volunteers
      .filter(v => v.email)
      .map(v => ({
        ...v,
        status: getVolStatus(v, volShifts[v.id]),
        shiftCount: (volShifts[v.id] || []).length,
        lang: isSpanish(v.congregation) ? 'es' : 'en',
      }))
      .sort((a, b) =>
        (a.lastName || '').localeCompare(b.lastName || '') ||
        (a.firstName || '').localeCompare(b.firstName || '')
      );
  }, [volunteers, volShifts]);

  // Filtered list for display — active filters are OR'd together
  const filtered = useMemo(() => {
    let list = enriched;
    if (activeFilters.size > 0) {
      list = list.filter(v => activeFilters.has(v.status));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(v =>
        `${v.firstName} ${v.lastName}`.toLowerCase().includes(q) ||
        (v.congregation || '').toLowerCase().includes(q) ||
        (v.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [enriched, activeFilters, search]);

  // Count per status key
  const counts = useMemo(() => {
    const c = {};
    enriched.forEach(v => { c[v.status] = (c[v.status] || 0) + 1; });
    return c;
  }, [enriched]);

  const selectNone = () => setSelected(new Set());
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // When filters change, auto-add those matching new filter combo to selection
  // (don't deselect already-selected volunteers outside the filter)
  const handleToggleFilter = (key) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Auto-select volunteers newly matching
        const toAdd = enriched.filter(v => v.status === key).map(v => v.id);
        setSelected(sel => {
          const ns = new Set(sel);
          toAdd.forEach(id => ns.add(id));
          return ns;
        });
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (activeFilters.size === 0) {
      // "All volunteers"
      setSelected(new Set(enriched.map(v => v.id)));
    } else {
      setSelected(new Set(filtered.map(v => v.id)));
    }
  };

  // Recipients = volunteers that are selected AND currently visible in the list
  const recipients = filtered.filter(v => selected.has(v.id));

  // Apply formatting to current language's body textarea
  const applyFormat = (type) => {
    const ta = bodyRef.current;
    if (!ta) return;
    const { newValue, newStart, newEnd } = applyFormatting(ta, type);
    setBodies(prev => ({ ...prev, [lang]: newValue }));
    // Restore selection after React re-render
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newStart, newEnd);
    });
  };

  // Build preview HTML for the current language
  const previewHtml = useMemo(() => {
    if (!preview) return '';
    const sampleName = recipients.find(v => v.lang === lang)?.firstName
      || recipients[0]?.firstName
      || 'Volunteer';
    const bodyText = bodies[lang].replace(/\[Volunteer Name\]/g, sampleName);
    return markdownToHtml(bodyText);
  }, [preview, bodies, lang, recipients]);

  const handleSend = async () => {
    if (recipients.length === 0 || !subjects.en.trim() || !bodies.en.trim()) return;

    const enRecipients = recipients.filter(v => v.lang === 'en');
    const esRecipients = recipients.filter(v => v.lang === 'es');
    const hasEs = esRecipients.length > 0 && subjects.es.trim() && bodies.es.trim();

    const lines = [
      `• ${enRecipients.length} English-language recipient${enRecipients.length !== 1 ? 's' : ''}`,
      ...(esRecipients.length > 0
        ? [`• ${esRecipients.length} Spanish-language recipient${esRecipients.length !== 1 ? 's' : ''}${hasEs ? '' : ' (⚠ no Spanish draft — will use English)'}`]
        : []),
    ];
    if (!window.confirm(`Send to ${recipients.length} volunteer${recipients.length !== 1 ? 's' : ''}?\n\n${lines.join('\n')}`)) return;

    setSending(true);
    setResult(null);

    try {
      // Build recipient lists — ES volunteers get ES draft if available, otherwise EN
      const buildList = (vols, draftLang) =>
        vols.map(v => ({ email: v.email, name: v.firstName }));

      const sends = [];

      if (enRecipients.length > 0) {
        sends.push(fetch('/api/broadcast-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipients: buildList(enRecipients),
            subject: subjects.en,
            body: bodies.en,
          }),
        }).then(r => r.json()));
      }

      if (esRecipients.length > 0) {
        const useEs = hasEs;
        sends.push(fetch('/api/broadcast-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipients: buildList(esRecipients),
            subject: useEs ? subjects.es : subjects.en,
            body: useEs ? bodies.es : bodies.en,
          }),
        }).then(r => r.json()));
      }

      const results = await Promise.all(sends);
      const totalSent = results.reduce((s, r) => s + (r.sent || 0), 0);
      const totalFailed = results.flatMap(r => r.failed || []);
      setResult({ success: true, sent: totalSent, failed: totalFailed });
    } catch (err) {
      setResult({ success: false, error: err.message });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading...</div></div>;
  }

  const body = bodies[lang];
  const subject = subjects[lang];

  const enCount = recipients.filter(v => v.lang === 'en').length;
  const esCount = recipients.filter(v => v.lang === 'es').length;

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Broadcast Email</h1>
        <p className="page-subtitle">Compose and send a message to selected volunteers.</p>
      </header>

      <div className="broadcast-layout">
        {/* ── Left panel: Recipient selection ──────────────────────────── */}
        <div className="broadcast-recipients-panel">
          <div className="broadcast-panel-header">
            <span className="broadcast-panel-title">Recipients</span>
            <span className="broadcast-selected-count">{selected.size} selected</span>
          </div>

          {/* Multi-select filter pills */}
          <div className="broadcast-filter-heading">
            Filter by status <span className="broadcast-filter-hint">(toggle multiple)</span>
          </div>
          <div className="broadcast-quick-filters">
            <button
              className={`broadcast-qf-btn${activeFilters.size === 0 ? ' active' : ''}`}
              onClick={() => { setActiveFilters(new Set()); setSelected(new Set(enriched.map(v => v.id))); }}
            >
              All
              <span className="broadcast-qf-count">{enriched.length}</span>
            </button>
            {ALL_FILTERS.map(f => (
              <button
                key={f.key}
                className={`broadcast-qf-btn${activeFilters.has(f.key) ? ' active' : ''}`}
                onClick={() => handleToggleFilter(f.key)}
              >
                {activeFilters.has(f.key) && <span style={{ fontSize: '10px', marginRight: '3px' }}>✓</span>}
                {f.label}
                <span className="broadcast-qf-count">{counts[f.key] || 0}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="broadcast-search-row">
            <input
              type="text"
              className="broadcast-search"
              placeholder="Search name, congregation, email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Select all / none */}
          <div className="broadcast-sel-row">
            <button className="broadcast-sel-btn" onClick={handleSelectAll}>
              Select All ({filtered.length})
            </button>
            <button className="broadcast-sel-btn" onClick={selectNone}>Deselect All</button>
          </div>

          {/* Volunteer list */}
          <div className="broadcast-vol-list">
            {filtered.length === 0 && (
              <p style={{ color: '#94a3b8', fontSize: '13px', padding: '12px' }}>No volunteers match.</p>
            )}
            {filtered.map(vol => {
              const meta = STATUS_META[vol.status] || STATUS_META.unassigned;
              const isChecked = selected.has(vol.id);
              return (
                <label key={vol.id} className={`broadcast-vol-row${isChecked ? ' checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleOne(vol.id)}
                    className="broadcast-vol-check"
                  />
                  <div className="broadcast-vol-info">
                    <span className="broadcast-vol-name">
                      {vol.lastName}, {vol.firstName}
                      {vol.lang === 'es' && <span className="broadcast-es-badge">ES</span>}
                    </span>
                    <span className="broadcast-vol-cong">{vol.congregation || '—'}</span>
                    <span className="broadcast-vol-email">{vol.email}</span>
                  </div>
                  <span
                    className="broadcast-vol-status"
                    style={{ color: meta.color, background: meta.bg }}
                  >
                    {meta.label}
                    {vol.shiftCount > 0 && ` (${vol.shiftCount})`}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* ── Right panel: Compose ─────────────────────────────────────── */}
        <div className="broadcast-compose-panel">
          <div className="broadcast-panel-header">
            <span className="broadcast-panel-title">Compose</span>
            <button
              className={`broadcast-preview-btn${preview ? ' active' : ''}`}
              onClick={() => setPreview(p => !p)}
            >
              {preview ? 'Edit' : 'Preview'}
            </button>
          </div>

          {/* Language tabs */}
          <div className="broadcast-lang-tabs">
            <button
              className={`broadcast-lang-tab${lang === 'en' ? ' active' : ''}`}
              onClick={() => setLang('en')}
            >
              English
              {enCount > 0 && <span className="broadcast-lang-count">{enCount}</span>}
            </button>
            <button
              className={`broadcast-lang-tab${lang === 'es' ? ' active' : ''}`}
              onClick={() => setLang('es')}
            >
              Spanish / Español
              {esCount > 0 && <span className="broadcast-lang-count">{esCount}</span>}
            </button>
            {esCount > 0 && !subjects.es.trim() && (
              <span className="broadcast-es-warning">⚠ Draft Spanish tab before sending</span>
            )}
          </div>

          {!preview ? (
            <>
              <div className="broadcast-field">
                <label className="broadcast-label">Subject</label>
                <input
                  type="text"
                  className="broadcast-subject-input"
                  value={subject}
                  onChange={e => setSubjects(prev => ({ ...prev, [lang]: e.target.value }))}
                  placeholder="Email subject line"
                />
              </div>

              <div className="broadcast-field">
                <div className="broadcast-toolbar">
                  <button
                    type="button"
                    className="broadcast-tb-btn"
                    title="Bold — **text**"
                    onClick={() => applyFormat('bold')}
                  ><strong>B</strong></button>
                  <button
                    type="button"
                    className="broadcast-tb-btn"
                    title="Italic — *text*"
                    onClick={() => applyFormat('italic')}
                  ><em>I</em></button>
                  <span className="broadcast-tb-sep" />
                  <button
                    type="button"
                    className="broadcast-tb-btn"
                    title="Bullet list"
                    onClick={() => applyFormat('ul')}
                  >• List</button>
                  <button
                    type="button"
                    className="broadcast-tb-btn"
                    title="Numbered list"
                    onClick={() => applyFormat('ol')}
                  >1. List</button>
                  <span className="broadcast-tb-sep" />
                  <span className="broadcast-tb-hint">Use [Volunteer Name] for personalized greeting</span>
                </div>
                <textarea
                  ref={bodyRef}
                  className="broadcast-body-textarea"
                  value={body}
                  onChange={e => setBodies(prev => ({ ...prev, [lang]: e.target.value }))}
                  placeholder="Type your message here…"
                  spellCheck
                />
                <div className="broadcast-charcount">{body.length} characters</div>
              </div>
            </>
          ) : (
            <div className="broadcast-preview-pane">
              <div className="broadcast-preview-subject">
                <strong>Subject:</strong> {subject || <em style={{ color: '#94a3b8' }}>No subject</em>}
              </div>
              <div className="broadcast-preview-body">
                <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: '560px', margin: '0 auto', color: '#1e293b' }}>
                  <div style={{ background: '#1e293b', color: 'white', padding: '20px 24px', borderRadius: '8px 8px 0 0' }}>
                    <strong style={{ fontSize: '18px' }}>SMPW Masters Event 2026</strong>
                    <p style={{ margin: '4px 0 0', opacity: 0.75, fontSize: '13px' }}>Augusta, Georgia — April 9–12, 2026</p>
                  </div>
                  <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', padding: '24px', borderRadius: '0 0 8px 8px' }}>
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    <p style={{ marginTop: '28px', paddingTop: '14px', borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: '12px' }}>
                      SMPW Scheduling Team – Augusta Masters
                    </p>
                  </div>
                </div>
              </div>
              <p className="broadcast-preview-note">
                Previewing {lang === 'en' ? 'English' : 'Spanish'} draft using{' '}
                {(recipients.find(v => v.lang === lang) || recipients[0])?.firstName || 'Volunteer'} as the sample name.
                Each recipient receives their own personalized copy.
              </p>
            </div>
          )}

          {/* Send section */}
          <div className="broadcast-send-section">
            {result && (
              <div className={`broadcast-result${result.success ? ' success' : ' error'}`}>
                {result.success ? (
                  <>
                    ✅ Sent to <strong>{result.sent}</strong> volunteer{result.sent !== 1 ? 's' : ''}
                    {result.failed?.length > 0 && (
                      <span style={{ color: '#b91c1c', marginLeft: '8px' }}>
                        · {result.failed.length} failed:{result.failed.map(f => ` ${f.name || f.email}`).join(',')}
                      </span>
                    )}
                  </>
                ) : (
                  <>✗ {result.error || 'Send failed'}</>
                )}
              </div>
            )}

            {recipients.length > 0 && (
              <div className="broadcast-recipient-summary">
                {enCount > 0 && <span className="broadcast-lang-pill en">{enCount} English</span>}
                {esCount > 0 && <span className="broadcast-lang-pill es">{esCount} Spanish</span>}
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  {recipients.slice(0, 3).map(v => `${v.firstName} ${v.lastName}`).join(', ')}
                  {recipients.length > 3 && ` +${recipients.length - 3} more`}
                </span>
              </div>
            )}

            <button
              className="btn btn-primary broadcast-send-btn"
              onClick={handleSend}
              disabled={sending || recipients.length === 0 || !subjects.en.trim() || !bodies.en.trim()}
            >
              {sending
                ? 'Sending…'
                : recipients.length > 0
                  ? `Send to ${recipients.length} Volunteer${recipients.length !== 1 ? 's' : ''}`
                  : 'Select Recipients First'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BroadcastEmail;
